import { TakeableChannel } from 'redux-saga';
import { ForkEffect, TakeEffect } from 'redux-saga/effects';
import {
  type SagaGenerator,
  all,
  call,
  debounce,
  delay,
  fork,
  join,
  put as typedPut,
  race as typedRace,
  select,
  take as typedTake,
  takeEvery as typedTakeEvery,
  takeLatest as typedTakeLatest,
  takeLeading as typedTakeLeading,
} from 'typed-redux-saga';

export const race = typedRace;

export { all, call, delay, select, type SagaGenerator, fork, join, debounce };

import { StoreEvent } from './event';

// Narrows a union based on matching (extending) { [TagProp]: Tag }
// TY: https://spin.atomicobject.com/2021/11/10/discriminated-unions-typescript-project/
// R: https://github.com/Microsoft/TypeScript/issues/17915
// R: https://stackoverflow.com/questions/46312206/narrowing-a-return-type-from-a-generic-discriminated-union-in-typescript
type Narrow<
  Union extends { [_ in TagProp]: string },
  TagProp extends string,
  Tag extends string,
> = Union extends { [_ in TagProp]: Tag }
  ? Union
  : Tag extends Union[TagProp]
  ? Omit<Union, TagProp> & { [_ in TagProp]: Tag }
  : never;

export type NarrowStoreEvent<TType extends StoreEvent['type']> = Narrow<
  StoreEvent,
  'type',
  TType
>;

type workerFn<T> = (
  event: T extends StoreEvent['type'] ? NarrowStoreEvent<T> : any,
) => any;

export const put = (event: StoreEvent) => typedPut(event);

export const take = typedTake as <
  T extends StoreEvent['type'] | TakeableChannel<any>,
>(
  event: T | T[],
) => SagaGenerator<Extract<StoreEvent, { type: T }>, TakeEffect>;

export const takeEvery = typedTakeEvery as <
  T extends StoreEvent['type'] | TakeableChannel<any>,
>(
  event: T | T[],
  worker: workerFn<T>,
) => SagaGenerator<never, ForkEffect<never>>;

export const takeLatest = typedTakeLatest as <
  T extends StoreEvent['type'] | TakeableChannel<any>,
>(
  event: T | T[],
  worker: workerFn<T>,
) => SagaGenerator<never, ForkEffect<never>>;

export const takeLeading = typedTakeLeading as <
  T extends StoreEvent['type'] | TakeableChannel<any>,
>(
  event: T | T[],
  worker: workerFn<T>,
) => SagaGenerator<never, ForkEffect<never>>;
